import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { categoriesAPI, businessesAPI, authAPI } from '@/lib/api';
import apiInstance from '@/lib/api';
import { isNativeApp, openExternalUrl, PUBLIC_WEB_URL } from '@/lib/capacitor';
import { countries, getCountryByCode } from '@/lib/countries';
import { getDetectedCountry } from '@/lib/detectCountry';
import { groupCategoriesBySuperGroup } from '@/lib/categoryGroups';
import { EVENTS_ENABLED } from '@/lib/featureFlags';
import { AgeVerification } from '@/components/AgeVerification';
import { CitySelector } from '@/components/CitySelector';
import GoogleMapDraggable, { GooglePlacesAutocomplete } from '@/components/GoogleMapComponents';
import { reverseGeocode, normalizeCityName } from '@/lib/reverseGeocode';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  ArrowLeft, ArrowRight, Mail, Lock, Phone, Building2, MapPin, 
  FileText, CreditCard, Upload, CheckCircle2, AlertTriangle, Eye, EyeOff,
  HelpCircle, CalendarX, Banknote, Globe, Search, Camera, Calendar, ShieldCheck, Info
} from 'lucide-react';
import CommissionBreakdownModal, { COMMISSION_TERMS_VERSION } from '@/components/CommissionBreakdownModal';

const STEPS = [
  { id: 'business', title: { es: 'Datos del negocio', en: 'Business info' } },
  { id: 'location', title: { es: 'Ubicación', en: 'Location' } },
  { id: 'account', title: { es: 'Cuenta y pago', en: 'Account & payment' } },
  { id: 'subscription', title: { es: 'Suscripción', en: 'Subscription' } },
];

export default function BusinessRegisterPage() {
  const { language } = useI18n();
  const { businessRegister } = useAuth();
  const navigate = useNavigate();
  
  const formatCurrencyMXN = (n) => `$${(Number(n) || 0).toFixed(2)} MXN`;
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loadingSubcats, setLoadingSubcats] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [ageValid, setAgeValid] = useState(false);
  const [ownerBirthDate, setOwnerBirthDate] = useState('');
  
  // File upload states
  const [ineFile, setIneFile] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [continueAfterAccept, setContinueAfterAccept] = useState(false);
  const [inePreview, setInePreview] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [registeredEmail, setRegisteredEmail] = useState('');
  
  const ineInputRef = useRef(null);
  const proofInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const coverInputRef = useRef(null);
  
  // Location search states
  const [locationQuery, setLocationQuery] = useState('');
  const [locationResults, setLocationResults] = useState([]);
  const [locationSearching, setLocationSearching] = useState(false);
  const locationDebounceRef = useRef(null);

  const [formData, setFormData] = useState({
    // Business info
    name: '',
    email: '',
    phone: '',
    description: '',
    category_id: '',
    subcategory_ids: [],
    custom_category_description: '',
    // Location
    address: '',
    address_number: '',
    interior_number: '',
    building_name: '',
    colony: '',
    city: '',
    state: '',
    country: getDetectedCountry(),
    zip_code: '',
    latitude: null,
    longitude: null,
    timezone: '',
    // Documents
    rfc: '',
    legal_name: '',
    tax_regime: '',
    ine_url: '',
    proof_of_address_url: '',
    // Account
    password: '',
    confirmPassword: '',
    clabe: '',
    bank_holder_name: '',
    bank_name: '',
    requires_deposit: false,
    deposit_amount: 100,
    cancellation_days: 1,
    cancellation_hours: 24,
    payout_schedule: 'monthly_cutoff_20',
    commission_terms_accepted: false,
    commission_terms_version: null,
    commission_terms_hash: null,
    commission_terms_snapshot: null,
    // Settings
    accepts_terms: false,
  });

  const selectedCountry = getCountryByCode(formData.country) || countries[0];

  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) return countries;
    const q = countrySearch.toLowerCase();
    return countries.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.nameEn.toLowerCase().includes(q) ||
      c.code.toLowerCase().includes(q) ||
      c.phone.includes(q)
    );
  }, [countrySearch]);

  useEffect(() => {
    loadCategories();
  }, []);

  // Google Places selection handler
  const handlePlaceSelect = (place) => {
    setFormData(prev => ({
      ...prev,
      address: place.street || place.formatted_address?.split(',')[0]?.replace(/\d+$/, '').trim() || '',
      address_number: place.street_number || prev.address_number,
      city: normalizeCityName(place.city) || prev.city,
      state: place.state || prev.state,
      zip_code: place.zip || prev.zip_code,
      latitude: place.lat,
      longitude: place.lng,
      colony: place.colony || prev.colony,
    }));
    setLocationQuery(place.formatted_address || '');
    toast.success(language === 'es' ? 'Ubicacion seleccionada' : 'Location selected');
  };

  const handleLocationQueryChange = (value) => {
    setLocationQuery(value);
  };

  const selectLocationResult = (loc) => {
    setFormData(prev => ({
      ...prev,
      address: loc.address,
      city: loc.city,
      state: loc.state,
      zip_code: loc.zip || prev.zip_code,
      latitude: loc.lat,
      longitude: loc.lng,
    }));
    setLocationQuery('');
    setLocationResults([]);
    toast.success(language === 'es' ? 'Ubicación seleccionada' : 'Location selected');
  };

  const loadCategories = async () => {
    try {
      const response = await categoriesAPI.getAll({ include_other: true });
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const autoCapitalize = (str) => str.replace(/(^|\s)\S/g, c => c.toUpperCase());

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const capitalizedFields = ['name', 'address', 'state', 'legal_name'];
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : capitalizedFields.includes(name) ? autoCapitalize(value) : value
    }));
  };

  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setPhoneNumber(digits);
    setFormData(prev => ({ ...prev, phone: `${selectedCountry.phone}${digits}` }));
  };

  const handleCountryChange = (code) => {
    const c = getCountryByCode(code);
    setFormData(prev => ({
      ...prev,
      country: code,
      phone: c ? `${c.phone}${phoneNumber}` : prev.phone,
    }));
  };

  const handleFileChange = (type, file) => {
    if (!file) return;
    
    const imageTypes = ['image/jpeg', 'image/png', 'image/webp'];
    const docTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    const allowedTypes = (type === 'logo' || type === 'cover') ? imageTypes : docTypes;
    
    if (!allowedTypes.includes(file.type)) {
      toast.error(language === 'es' 
        ? (type === 'logo' || type === 'cover' ? 'Solo se permiten archivos JPG, PNG o WebP' : 'Solo se permiten archivos JPG, PNG, WebP o PDF')
        : 'Only JPG, PNG, WebP files allowed');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === 'es' ? 'El archivo no debe exceder 5MB' : 'File must not exceed 5MB');
      return;
    }
    
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (type === 'ine') setInePreview(e.target.result);
        else if (type === 'proof') setProofPreview(e.target.result);
        else if (type === 'logo') setLogoPreview(e.target.result);
        else if (type === 'cover') setCoverPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      if (type === 'ine') setInePreview('pdf');
      else if (type === 'proof') setProofPreview('pdf');
    }
    
    if (type === 'ine') setIneFile(file);
    else if (type === 'proof') setProofFile(file);
    else if (type === 'logo') setLogoFile(file);
    else if (type === 'cover') setCoverFile(file);
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await apiInstance.post('/upload/public', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data.url;
    } catch {
      return null;
    }
  };

  const validateStep = () => {
    switch (currentStep) {
      case 0: // Business info
        if (!formData.name || !formData.email || !formData.phone || !formData.category_id || !formData.description) {
          toast.error(language === 'es' 
            ? 'Completa todos los campos obligatorios' 
            : 'Complete all required fields');
          return false;
        }
        if (!logoFile && !coverFile) {
          toast.error(language === 'es' ? 'Sube al menos una imagen: logo o foto de tu negocio' : 'Upload at least one image: logo or business photo');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          toast.error(language === 'es' ? 'Email inválido' : 'Invalid email');
          return false;
        }
        if (phoneNumber.length < 7) {
          toast.error(language === 'es' ? 'El número de teléfono debe tener al menos 7 dígitos' : 'Phone number must have at least 7 digits');
          return false;
        }
        if (!ageValid) {
          toast.error(language === 'es' ? 'El responsable debe tener al menos 16 años' : 'The owner must be at least 16 years old');
          return false;
        }
        return true;
        
      case 1: // Location
        if (!formData.address || !formData.city || !formData.state || !formData.zip_code) {
          toast.error(language === 'es' 
            ? 'Completa todos los campos de ubicación' 
            : 'Complete all location fields');
          return false;
        }
        if (!formData.colony || !formData.colony.trim()) {
          toast.error(language === 'es'
            ? 'La colonia es obligatoria'
            : 'Neighborhood is required');
          return false;
        }
        return true;

      // NOTE: the former "case 2 — Documents" (RFC, razón social, INE,
      // régimen fiscal) was removed in 2026-02. We now capture that info
      // on-demand later: the ActivateDepositsWizard asks for RFC + CLABE
      // when the business turns on anticipos, and admins can request the
      // rest from the Settings tab for high-risk cases.

      case 2: // Account
        if (!formData.password) {
          toast.error(language === 'es' 
            ? 'La contraseña es obligatoria' 
            : 'Password is required');
          return false;
        }
        if (formData.password.length < 8) {
          toast.error(language === 'es' 
            ? 'La contraseña debe tener al menos 8 caracteres' 
            : 'Password must be at least 8 characters');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error(language === 'es' 
            ? 'Las contraseñas no coinciden' 
            : 'Passwords do not match');
          return false;
        }
        // ── Phase T: bank data is required ONLY when the business chose
        // "Requiero anticipo". If they don't require a deposit we have no
        // reason to store a CLABE, since we never deposit anything to them.
        if (formData.requires_deposit) {
          if (!formData.clabe) {
            toast.error(language === 'es'
              ? 'La CLABE es obligatoria — es donde depositaremos tus anticipos'
              : 'CLABE is required — that\'s where we\'ll deposit your funds');
            return false;
          }
          if (!/^\d{18}$/.test(formData.clabe)) {
            toast.error(language === 'es'
              ? 'La CLABE debe tener exactamente 18 dígitos'
              : 'CLABE must be exactly 18 digits');
            return false;
          }
          if (!formData.bank_holder_name || formData.bank_holder_name.trim().length < 3) {
            toast.error(language === 'es'
              ? 'Ingresa el nombre completo del titular de la cuenta'
              : 'Enter the full account holder name');
            return false;
          }
          if (!formData.bank_name) {
            toast.error(language === 'es'
              ? 'Selecciona tu banco'
              : 'Select your bank');
            return false;
          }
        }
        // Deposit validation: if enabled, must be >= 100 MXN
        if (formData.requires_deposit && Number(formData.deposit_amount) < 100) {
          toast.error(language === 'es' 
            ? 'El monto del anticipo debe ser al menos $100 MXN' 
            : 'Deposit amount must be at least $100 MXN');
          return false;
        }
        // NOTE: Commission terms acceptance gate is enforced INSIDE handleNext
        // (force-opens the modal instead of just toasting), so it is NOT validated here.
        return true;
        
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep()) return;
    
    if (currentStep === 2) {
      // Force-acceptance gate: if business chose anticipo but did NOT click
      // "Ver desglose completo", open the modal automatically. The modal's
      // onAccept callback will retry handleNext once accepted, completing
      // registration in a single flow.
      if (formData.requires_deposit && !formData.commission_terms_accepted) {
        toast.info(language === 'es'
          ? 'Antes de registrar, lee y acepta el desglose de comisiones.'
          : 'Before registering, please read and accept the fee breakdown.');
        setContinueAfterAccept(true);
        setShowCommissionModal(true);
        return;
      }
      // Step 3 (Account) → register the business, then go to Step 4
      handleRegister();
    } else if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep === 3) return;
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleRegister = async (overrides = {}) => {
    if (!formData.accepts_terms) {
      toast.error(language === 'es'
        ? 'Debes aceptar los Terminos y el Aviso de Privacidad para continuar'
        : 'You must accept the Terms and Privacy Notice to continue');
      return;
    }
    setLoading(true);
    try {
      let ineUrl = '';
      let proofUrl = '';
      let logoUrl = null;
      let coverUrl = null;
      
      // Upload files
      if (ineFile) ineUrl = await uploadFile(ineFile);
      if (proofFile) proofUrl = await uploadFile(proofFile);
      if (logoFile) logoUrl = await uploadFile(logoFile);
      if (coverFile) coverUrl = await uploadFile(coverFile);
      
      const fullAddress = formData.address_number 
        ? `${formData.address} ${formData.address_number}` 
        : formData.address;
      // Build full street_full label with interior + building if provided
      const addressExtras = [
        formData.interior_number ? `Int. ${formData.interior_number}` : null,
        formData.building_name || null,
      ].filter(Boolean);
      const fullAddressLabel = addressExtras.length
        ? `${fullAddress}, ${addressExtras.join(', ')}`
        : fullAddress;

      // Merge any inline overrides (e.g. commission terms just-accepted in the
      // forced-modal flow whose state may not have flushed yet)
      const effectiveTermsAccepted = overrides.commission_terms_accepted ?? formData.commission_terms_accepted;
      const effectiveTermsVersion = overrides.commission_terms_version ?? formData.commission_terms_version;
      const effectiveTermsHash = overrides.commission_terms_hash ?? formData.commission_terms_hash;
      const effectiveTermsSnapshot = overrides.commission_terms_snapshot ?? formData.commission_terms_snapshot;

      const registerData = {
        name: formData.name, email: formData.email, password: formData.password,
        phone: formData.phone, description: formData.description,
        category_id: formData.category_id,
        subcategory_ids: formData.subcategory_ids || [],
        address: fullAddressLabel,
        colony: formData.colony || '',
        interior_number: formData.interior_number || '',
        building_name: formData.building_name || '',
        city: formData.city, state: formData.state, country: formData.country,
        zip_code: formData.zip_code, rfc: (formData.rfc || '').toUpperCase(),
        legal_name: formData.legal_name || '', ine_url: ineUrl,
        proof_of_address_url: proofUrl,
        // ── Phase T: bank data ALWAYS sent (used for payouts, not billing)
        clabe: formData.clabe || '',
        bank_holder_name: (formData.bank_holder_name || '').trim(),
        bank_name: formData.bank_name || '',
        requires_deposit: formData.requires_deposit,
        deposit_amount: formData.requires_deposit ? Number(formData.deposit_amount) : 100,
        cancellation_days: Math.max(1, Math.ceil((Number(formData.cancellation_hours) || 24) / 24)),
        cancellation_hours: Math.min(72, Math.max(1, Number(formData.cancellation_hours) || 24)),
        payout_schedule: 'monthly_cutoff_20',
        commission_terms_accepted: formData.requires_deposit ? !!effectiveTermsAccepted : false,
        commission_terms_version: formData.requires_deposit && effectiveTermsAccepted ? effectiveTermsVersion : null,
        commission_terms_hash: formData.requires_deposit && effectiveTermsAccepted ? effectiveTermsHash : null,
        commission_terms_snapshot: formData.requires_deposit && effectiveTermsAccepted ? effectiveTermsSnapshot : null,
        tax_regime: formData.country !== 'US' ? formData.tax_regime || null : null,
        owner_birth_date: ownerBirthDate,
        logo_url: logoUrl,
        cover_photo: coverUrl,
        latitude: formData.latitude,
        longitude: formData.longitude,
        custom_category_description: formData.custom_category_description || '',
        timezone: formData.timezone || (formData.country === 'US' ? 'America/New_York' : 'America/Mexico_City'),
      };
      
      // ── Phase T: no Stripe checkout at signup ─────────────────────
      // The backend creates the business + trial + returns a JWT token.
      // We store the token and navigate directly to the trial success step.
      const response = await businessRegister(registerData);
      setRegisteredEmail(formData.email);
      // If the API returned a token, persist it so the user is logged in
      // immediately (allows accessing the trial dashboard on next click).
      try {
        const token = response?.data?.token || response?.token;
        if (token) localStorage.setItem('token', token);
      } catch (_) { /* non-fatal */ }
      toast.success(
        language === 'es'
          ? '¡Registro exitoso! Empieza tu prueba gratuita de 30 días.'
          : 'Registered! Your 30-day free trial has started.'
      );
      setCurrentStep(3);
    } catch (error) {
      const message = error.response?.data?.detail || (language === 'es' ? 'Error al registrar negocio' : 'Error registering business');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    // ── Apple/Google commission avoidance ──
    // Defensive guard: if somehow the user reaches this page from within the
    // native app (deep link, etc.), redirect to the web flow before opening
    // Stripe Checkout. Apple's review team rejects in-app subscription flows
    // that route through external payment processors.
    if (isNativeApp()) {
      await openExternalUrl(`${PUBLIC_WEB_URL}/business/register?from=app`);
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const emailToUse = registeredEmail || formData.email;
      const res = await authAPI.createRegistrationSubscription({ email: emailToUse, origin_url: originUrl });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('No checkout URL');
      }
    } catch (error) {
      console.error('Subscription error:', error?.response?.data || error);
      toast.error(language === 'es' ? 'Error al conectar con el procesador de pagos. Intenta de nuevo o contacta soporte.' : 'Error connecting to payment processor. Try again or contact support.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    handleSubscribe();
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen px-4 py-10 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800" data-testid="business-register-page">
      <div className="max-w-2xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6"
          data-testid="back-to-home"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {language === 'es' ? 'Volver al inicio' : 'Back to home'}
        </Button>

        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <Link to="/" className="inline-block mb-4">
              <span className="text-3xl font-heading font-extrabold">
                Book<span className="text-[#F05D5E]">via</span>
              </span>
            </Link>
            <CardTitle className="text-2xl font-heading">
              {language === 'es' ? 'Registra tu negocio' : 'Register your business'}
            </CardTitle>
            <CardDescription>
              {language === 'es' 
                ? 'Únete a la plataforma de reservas más grande de México' 
                : 'Join the largest booking platform in Mexico'}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {/* Progress indicator */}
            <div className="mb-8">
              <div className="flex justify-between mb-2">
                {STEPS.map((step, index) => {
                  const isPast = index < currentStep;
                  const isActive = index === currentStep;
                  // Allow jumping back to any past step (not forward) before final submit (step 4)
                  const canJump = isPast && currentStep < 3;
                  return (
                    <button
                      type="button"
                      key={step.id}
                      onClick={canJump ? () => setCurrentStep(index) : undefined}
                      disabled={!canJump}
                      title={canJump ? (language === 'es' ? `Volver a ${step.title[language]}` : `Back to ${step.title[language]}`) : undefined}
                      data-testid={`stepper-jump-${index}`}
                      className={`flex flex-col items-center ${isPast || isActive ? 'text-[#F05D5E]' : 'text-muted-foreground'} ${canJump ? 'cursor-pointer hover:opacity-80 transition-opacity' : 'cursor-default'}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                        ${isPast ? 'bg-[#F05D5E] text-white' :
                          isActive ? 'border-2 border-[#F05D5E] text-[#F05D5E]' :
                          'border-2 border-muted-foreground'}`}
                      >
                        {isPast ? <CheckCircle2 className="h-5 w-5" /> : index + 1}
                      </div>
                      <span className="text-xs mt-1 hidden sm:block">{step.title[language]}</span>
                    </button>
                  );
                })}
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            <form onSubmit={handleSubmit}>
              {/* Step 1: Business Info */}
              {currentStep === 0 && (
                <div className="space-y-4" data-testid="step-business">
                  <div className="space-y-2">
                    <Label htmlFor="name">
                      {language === 'es' ? 'Nombre del negocio' : 'Business name'} *
                    </Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                      <Input
                        id="name"
                        name="name"
                        placeholder={language === 'es' ? 'Spa Relax' : 'Relax Spa'}
                        value={formData.name}
                        onChange={handleChange}
                        className="pl-10 h-12"
                        required
                        data-testid="business-name-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">{language === 'es' ? 'Correo electrónico' : 'Email'} *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="negocio@email.com"
                          value={formData.email}
                          onChange={handleChange}
                          className="pl-10 h-12"
                          required
                          data-testid="business-email-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Country */}
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'País' : 'Country'} *</Label>
                    <Select value={formData.country} onValueChange={handleCountryChange}>
                      <SelectTrigger className="h-12" data-testid="business-country-select">
                        <SelectValue>
                          <span className="flex items-center gap-2">
                            <span className="text-lg leading-none">{selectedCountry.flag}</span>
                            <span>{language === 'es' ? selectedCountry.name : selectedCountry.nameEn}</span>
                          </span>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-h-64">
                        <div className="sticky top-0 bg-popover p-2 border-b">
                          <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <input
                              type="text"
                              placeholder={language === 'es' ? 'Buscar país...' : 'Search country...'}
                              value={countrySearch}
                              onChange={(e) => setCountrySearch(e.target.value)}
                              className="w-full pl-8 pr-3 py-2 text-sm rounded-md border bg-transparent outline-none focus:ring-1 focus:ring-ring"
                              data-testid="business-country-search-input"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>
                        {filteredCountries.map(c => (
                          <SelectItem key={c.code} value={c.code} data-testid={`biz-country-option-${c.code}`}>
                            <span className="flex items-center gap-2">
                              <span className="text-lg leading-none">{c.flag}</span>
                              <span className="flex-1">{language === 'es' ? c.name : c.nameEn}</span>
                              <span className="text-muted-foreground text-xs ml-1">({c.phone})</span>
                            </span>
                          </SelectItem>
                        ))}
                        {filteredCountries.length === 0 && (
                          <div className="py-4 text-center text-sm text-muted-foreground">
                            {language === 'es' ? 'No se encontraron países' : 'No countries found'}
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Phone with country code */}
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Teléfono' : 'Phone'} *</Label>
                    <div className="flex gap-0 items-center rounded-md border border-input focus-within:ring-1 focus-within:ring-ring h-12 overflow-hidden">
                      <div className="flex items-center gap-1.5 px-3 bg-muted/50 h-full border-r shrink-0 select-none">
                        <span className="text-base leading-none">{selectedCountry.flag}</span>
                        <span className="text-sm font-medium text-foreground">{selectedCountry.phone}</span>
                      </div>
                      <input
                        id="biz-phone"
                        name="phone"
                        type="tel"
                        inputMode="numeric"
                        placeholder={formData.country === 'US' ? '(555) 123-4567' : '55 1234 5678'}
                        value={phoneNumber}
                        onChange={handlePhoneChange}
                        maxLength={formData.country === 'US' ? 10 : 10}
                        className="flex-1 h-full px-3 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
                        required
                        data-testid="business-phone-input"
                      />
                      <span className="text-xs text-muted-foreground pr-3 shrink-0">{phoneNumber.length}/10</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Categoría' : 'Category'} *</Label>
                    <Select
                      value={formData.category_id}
                      onValueChange={async (value) => {
                        setFormData(prev => ({ ...prev, category_id: value, subcategory_ids: [] }));
                        setSubcategories([]);
                        setLoadingSubcats(true);
                        try {
                          const res = await categoriesAPI.getSubcategories(value);
                          setSubcategories(res.data || []);
                        } catch (e) {
                          console.error('Error loading subcategories:', e);
                        } finally {
                          setLoadingSubcats(false);
                        }
                      }}
                    >
                      <SelectTrigger className="h-12" data-testid="category-select">
                        <SelectValue placeholder={language === 'es' ? 'Selecciona una categoría' : 'Select a category'} />
                      </SelectTrigger>
                      <SelectContent className="max-h-[60vh]">
                        {groupCategoriesBySuperGroup(categories, { includeHidden: false, includeOther: true }).map(group => (
                          <SelectGroup key={group.slug}>
                            <SelectLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
                              {language === 'es' ? group.name_es : group.name_en}
                            </SelectLabel>
                            {group.items.map(cat => (
                              <SelectItem key={cat.id} value={cat.id} data-testid={`category-option-${cat.slug}`}>
                                {language === 'es' ? cat.name_es : cat.name_en}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Subcategory multi-select chips (Phase H) */}
                  {formData.category_id && subcategories.length > 0 && (
                    <div className="space-y-2" data-testid="subcategory-chips-container">
                      <Label>
                        {language === 'es' ? 'Servicios que ofreces' : 'Services you offer'}
                        <span className="text-xs text-muted-foreground ml-2">
                          {language === 'es' ? '(opcional, máximo 3)' : '(optional, max 3)'}
                        </span>
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {subcategories.map(sub => {
                          const selected = formData.subcategory_ids.includes(sub.id);
                          const disabled = !selected && formData.subcategory_ids.length >= 3;
                          return (
                            <button
                              key={sub.id}
                              type="button"
                              disabled={disabled}
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                subcategory_ids: selected
                                  ? prev.subcategory_ids.filter(id => id !== sub.id)
                                  : [...prev.subcategory_ids, sub.id],
                              }))}
                              data-testid={`subcategory-chip-${sub.slug}`}
                              className={`px-3 py-1.5 rounded-full text-sm border transition
                                ${selected ? 'bg-[#F05D5E] text-white border-[#F05D5E]' :
                                  disabled ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed' :
                                  'bg-white text-slate-700 border-slate-300 hover:border-[#F05D5E] hover:text-[#F05D5E]'}`}
                            >
                              {language === 'es' ? sub.name_es : sub.name_en}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {language === 'es'
                          ? `${formData.subcategory_ids.length}/3 seleccionadas`
                          : `${formData.subcategory_ids.length}/3 selected`}
                      </p>
                    </div>
                  )}
                  {loadingSubcats && (
                    <p className="text-xs text-muted-foreground">{language === 'es' ? 'Cargando subcategorías...' : 'Loading subcategories...'}</p>
                  )}

                  {/* Custom category description when "Otro" is selected */}
                  {categories.find(c => c.id === formData.category_id && (c.slug === 'otro' || c.name_es?.toLowerCase() === 'otro' || c.name_en?.toLowerCase() === 'other')) && (
                    <div className="space-y-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200" data-testid="custom-category-field">
                      <Label>{language === 'es' ? 'Describe tu tipo de negocio' : 'Describe your business type'} *</Label>
                      <Input
                        placeholder={language === 'es' ? 'Ej: Tatuajes y piercings, Cerrajeria, etc.' : 'Ex: Tattoos and piercings, Locksmith, etc.'}
                        value={formData.custom_category_description}
                        onChange={e => setFormData(prev => ({ ...prev, custom_category_description: e.target.value }))}
                        data-testid="custom-category-input"
                      />
                      <p className="text-xs text-amber-700 dark:text-amber-400">
                        {language === 'es'
                          ? 'Tu solicitud sera revisada y se te asignara la categoria correcta.'
                          : 'Your request will be reviewed and you will be assigned the correct category.'}
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="description">{language === 'es' ? 'Descripcion' : 'Description'} *</Label>
                    <Textarea
                      id="description"
                      name="description"
                      placeholder={language === 'es' 
                        ? 'Describe tu negocio, servicios y experiencia...' 
                        : 'Describe your business, services and experience...'}
                      value={formData.description}
                      onChange={handleChange}
                      className="min-h-[100px]"
                      required
                      data-testid="business-description-input"
                    />
                  </div>

                  {/* Age Verification for business owner */}
                  <div className="rounded-xl border bg-muted/30 p-4">
                    <p className="text-xs text-muted-foreground mb-3">
                      {language === 'es' 
                        ? 'Edad del responsable del negocio' 
                        : 'Age of the business owner'}
                    </p>
                    <AgeVerification
                      onDateChange={setOwnerBirthDate}
                      onAgeValid={setAgeValid}
                      minAge={16}
                    />
                  </div>

                  {/* Image uploads */}
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Imágenes de tu negocio' : 'Business images'} *</Label>
                    <p className="text-xs text-muted-foreground">{language === 'es' ? 'Sube al menos una: logo o foto de tu negocio' : 'Upload at least one: logo or business photo'}</p>
                    
                    <div className="grid sm:grid-cols-2 gap-3">
                      {/* Logo upload */}
                      <div
                        className={`relative border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors hover:border-[#F05D5E]/50 ${logoPreview ? 'border-green-300 bg-green-50/50 dark:bg-green-900/10' : 'border-muted-foreground/20'}`}
                        onClick={() => logoInputRef.current?.click()}
                        data-testid="logo-upload-area"
                      >
                        <input
                          ref={logoInputRef}
                          type="file"
                          className="hidden"
                          accept="image/jpeg,image/png,image/webp,.jfif"
                          onChange={(e) => handleFileChange('logo', e.target.files[0])}
                          data-testid="logo-file-input"
                        />
                        {logoPreview ? (
                          <div className="flex flex-col items-center gap-2">
                            <img src={logoPreview} alt="Logo" className="h-20 w-20 rounded-full object-cover border-2 border-green-300" />
                            <p className="text-xs font-medium text-green-700 dark:text-green-300">{language === 'es' ? 'Logo subido' : 'Logo uploaded'}</p>
                            <p className="text-[10px] text-muted-foreground">{language === 'es' ? 'Clic para cambiar' : 'Click to change'}</p>
                          </div>
                        ) : (
                          <div className="py-3">
                            <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-2">
                              <Upload className="h-6 w-6 text-muted-foreground/40" />
                            </div>
                            <p className="text-sm font-medium">{language === 'es' ? 'Logo' : 'Logo'}</p>
                            <p className="text-[10px] text-muted-foreground">{language === 'es' ? 'Se mostrará en el perfil circular' : 'Shown in circular profile'}</p>
                          </div>
                        )}
                      </div>

                      {/* Cover photo upload */}
                      <div
                        className={`relative border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors hover:border-[#F05D5E]/50 ${coverPreview ? 'border-green-300 bg-green-50/50 dark:bg-green-900/10' : 'border-muted-foreground/20'}`}
                        onClick={() => coverInputRef.current?.click()}
                        data-testid="cover-upload-area"
                      >
                        <input
                          ref={coverInputRef}
                          type="file"
                          className="hidden"
                          accept="image/jpeg,image/png,image/webp,.jfif"
                          onChange={(e) => handleFileChange('cover', e.target.files[0])}
                          data-testid="cover-file-input"
                        />
                        {coverPreview ? (
                          <div className="flex flex-col items-center gap-2">
                            <img src={coverPreview} alt="Cover" className="h-20 w-full rounded-lg object-cover border-2 border-green-300" />
                            <p className="text-xs font-medium text-green-700 dark:text-green-300">{language === 'es' ? 'Foto subida' : 'Photo uploaded'}</p>
                            <p className="text-[10px] text-muted-foreground">{language === 'es' ? 'Clic para cambiar' : 'Click to change'}</p>
                          </div>
                        ) : (
                          <div className="py-3">
                            <div className="h-14 w-full max-w-[80px] rounded-lg bg-muted flex items-center justify-center mx-auto mb-2">
                              <Camera className="h-6 w-6 text-muted-foreground/40" />
                            </div>
                            <p className="text-sm font-medium">{language === 'es' ? 'Foto del negocio' : 'Business photo'}</p>
                            <p className="text-[10px] text-muted-foreground">{language === 'es' ? 'Se mostrará en búsquedas' : 'Shown in search results'}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Location */}
              {currentStep === 1 && (
                <div className="space-y-4" data-testid="step-location">
                  {/* Google Places Autocomplete */}
                  <div className="space-y-2">
                    <Label>{language === 'es' ? 'Buscar direccion' : 'Search address'}</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10" />
                      <GooglePlacesAutocomplete
                        value={locationQuery}
                        onChange={handleLocationQueryChange}
                        onSelect={handlePlaceSelect}
                        placeholder={language === 'es' ? 'Escribe tu direccion para buscar en el mapa...' : 'Type your address to search on map...'}
                        countryCode={formData.country?.toLowerCase() || 'mx'}
                        language={language}
                        className="flex h-12 w-full rounded-md border border-input bg-background pl-10 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      />
                    </div>
                    <div className="flex items-start gap-2 text-xs text-muted-foreground bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-2.5">
                      <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                      <p className="leading-snug">
                        {language === 'es'
                          ? 'Asegúrate de que tu ubicación coincida con el pin en el mapa. Si no aparece, prueba con el nombre completo de la calle o avenida (ej: escribe "Abraham Lincoln" en lugar de "Lincoln").'
                          : 'Make sure your location matches the pin on the map. If it doesn\'t appear, try the full street/avenue name (e.g. type "Abraham Lincoln" instead of "Lincoln").'}
                      </p>
                    </div>
                  </div>

                  {/* Interactive Google Map */}
                  <div className="rounded-xl border overflow-hidden" data-testid="registration-map-preview">
                    <GoogleMapDraggable
                      lat={formData.latitude}
                      lng={formData.longitude}
                      height="220px"
                      onPositionChange={async (lat, lng) => {
                        // Update coordinates immediately so the marker moves
                        setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
                        // Reverse-geocode to auto-fill city/state/country/zip so the
                        // dashboard search-by-city and SEO pages get consistent data.
                        try {
                          const info = await reverseGeocode(lat, lng);
                          if (info) {
                            setFormData(prev => ({
                              ...prev,
                              // Only overwrite if the field is empty OR the map moved
                              // enough that the new value is clearly different — this
                              // avoids clobbering something the user just typed.
                              city: normalizeCityName(info.city) || prev.city,
                              state: info.state || prev.state,
                              country: info.country || prev.country || 'MX',
                              zip_code: info.zip_code || prev.zip_code,
                              address: prev.address || info.street || info.formatted_address || '',
                            }));
                          }
                        } catch { /* silent — reverse-geocode is best-effort */ }
                      }}
                    />
                    <p className="text-xs text-muted-foreground px-3 py-1.5 bg-muted/30 text-center">
                      {language === 'es' ? 'Arrastra el marcador o haz clic para ajustar la ubicación exacta' : 'Drag the marker or click to adjust the exact location'}
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-2 space-y-2">
                      <Label htmlFor="address">{language === 'es' ? 'Calle' : 'Street'} *</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                        <Input
                          id="address"
                          name="address"
                          placeholder={language === 'es' ? 'Ej: Av. Rio Tam' : 'E.g.: Main St'}
                          value={formData.address}
                          onChange={handleChange}
                          className="pl-10 h-12"
                          required
                          data-testid="address-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="address_number" className="text-xs sm:text-sm">
                        {formData.country === 'US' ? 'Suite / Apt' : (language === 'es' ? 'Num. ext.' : 'Number')}
                      </Label>
                      <Input
                        id="address_number"
                        name="address_number"
                        placeholder="123"
                        value={formData.address_number || ''}
                        onChange={handleChange}
                        className="h-12"
                        data-testid="address-number-input"
                      />
                      <p className="text-[10px] text-muted-foreground leading-tight">
                        {language === 'es' ? 'Se autocompleta del mapa' : 'Auto-filled from map'}
                      </p>
                    </div>
                  </div>

                  {/* Colonia (required) */}
                  <div className="space-y-2">
                    <Label htmlFor="colony">{language === 'es' ? 'Colonia' : 'Neighborhood'} *</Label>
                    <Input
                      id="colony"
                      name="colony"
                      placeholder={language === 'es' ? 'Ej: Centro, Del Valle, Madero' : 'E.g. Downtown, Midtown'}
                      value={formData.colony || ''}
                      onChange={handleChange}
                      className="h-12"
                      required
                      data-testid="colony-input"
                    />
                  </div>

                  {/* Interior + Building (both optional) */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="interior_number" className="text-xs sm:text-sm">
                        {language === 'es' ? 'Núm. interior (opcional)' : 'Interior # (optional)'}
                      </Label>
                      <Input
                        id="interior_number"
                        name="interior_number"
                        placeholder={language === 'es' ? 'Ej: 5B, Local 12' : 'E.g. 5B, Suite 12'}
                        value={formData.interior_number || ''}
                        onChange={handleChange}
                        className="h-12"
                        data-testid="interior-number-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="building_name" className="text-xs sm:text-sm">
                        {language === 'es' ? 'Plaza / Torre / Edificio (opcional)' : 'Plaza / Tower / Building (optional)'}
                      </Label>
                      <Input
                        id="building_name"
                        name="building_name"
                        placeholder={language === 'es' ? 'Ej: Plaza Galerías, Torre A' : 'E.g. Galerías Plaza, Tower A'}
                        value={formData.building_name || ''}
                        onChange={handleChange}
                        className="h-12"
                        data-testid="building-name-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === 'es' ? 'Ciudad' : 'City'} *</Label>
                      <CitySelector
                        countryCode={formData.country}
                        value={formData.city}
                        onChange={(city) => setFormData(prev => ({ ...prev, city }))}
                        onCityData={(cityObj) => {
                          if (cityObj.state && !formData.state) {
                            setFormData(prev => ({ ...prev, city: cityObj.name, state: cityObj.state }));
                          }
                        }}
                        showDemand
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">{language === 'es' ? 'Estado / Region' : 'State'} *</Label>
                      <Input
                        id="state"
                        name="state"
                        placeholder={formData.country === 'US' ? 'California' : (language === 'es' ? 'Ej: Jalisco' : 'E.g.: California')}
                        value={formData.state}
                        onChange={handleChange}
                        className="h-12"
                        required
                        data-testid="state-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="zip_code">{language === 'es' ? 'Codigo postal' : 'ZIP code'} *</Label>
                      <Input
                        id="zip_code"
                        name="zip_code"
                        placeholder={formData.country === 'US' ? '90210' : '01234'}
                        value={formData.zip_code}
                        onChange={handleChange}
                        className="h-12"
                        maxLength={formData.country === 'US' ? 5 : 5}
                        required
                        data-testid="zipcode-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'es' ? 'País' : 'Country'}</Label>
                      <Select 
                        value={formData.country} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
                      >
                        <SelectTrigger className="h-12">
                          <SelectValue>
                            <span className="flex items-center gap-2">
                              <span className="text-lg leading-none">{selectedCountry.flag}</span>
                              <span>{language === 'es' ? selectedCountry.name : selectedCountry.nameEn}</span>
                            </span>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="max-h-52">
                          {countries.map(c => (
                            <SelectItem key={c.code} value={c.code}>
                              <span className="flex items-center gap-2">
                                <span className="text-lg leading-none">{c.flag}</span>
                                <span>{language === 'es' ? c.name : c.nameEn}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Timezone selector for USA */}
                  {formData.country === 'US' && (
                    <div className="space-y-2">
                      <Label>{language === 'es' ? 'Zona horaria' : 'Timezone'} *</Label>
                      <Select
                        value={formData.timezone}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, timezone: value }))}
                      >
                        <SelectTrigger className="h-12" data-testid="timezone-select">
                          <SelectValue placeholder={language === 'es' ? 'Selecciona zona horaria' : 'Select timezone'} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="America/New_York">Eastern (New York, Miami, Atlanta)</SelectItem>
                          <SelectItem value="America/Chicago">Central (Chicago, Houston, Dallas)</SelectItem>
                          <SelectItem value="America/Denver">Mountain (Denver, Phoenix, Salt Lake)</SelectItem>
                          <SelectItem value="America/Los_Angeles">Pacific (Los Angeles, San Francisco, Seattle)</SelectItem>
                          <SelectItem value="America/Anchorage">Alaska</SelectItem>
                          <SelectItem value="Pacific/Honolulu">Hawaii</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Account & Payment */}
              {currentStep === 2 && (
                <div className="space-y-4" data-testid="step-account">
                  <div className="space-y-2">
                    <Label htmlFor="password">{language === 'es' ? 'Contraseña' : 'Password'} *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleChange}
                        className="pl-10 pr-10 h-12"
                        required
                        data-no-capitalize="true"
                        data-testid="password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {language === 'es' ? 'Mínimo 8 caracteres' : 'Minimum 8 characters'}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">
                      {language === 'es' ? 'Confirmar contraseña' : 'Confirm password'} *
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="pl-10 h-12"
                        required
                        data-no-capitalize="true"
                        data-testid="confirm-password-input"
                      />
                    </div>
                  </div>

                  <label className="flex items-start gap-2 pt-2 cursor-pointer select-none" data-testid="business-accept-terms-label">
                    <input
                      type="checkbox"
                      name="accepts_terms"
                      checked={!!formData.accepts_terms}
                      onChange={(e) => setFormData(prev => ({ ...prev, accepts_terms: e.target.checked }))}
                      className="mt-1 h-4 w-4 accent-[#F05D5E] cursor-pointer"
                      data-testid="business-accept-terms"
                    />
                    <span className="text-xs text-muted-foreground leading-relaxed">
                      {language === 'es' ? (
                        <>
                          Como duen@ del negocio, acepto los{' '}
                          <Link to="/terms" target="_blank" className="text-[#F05D5E] font-medium hover:underline">Terminos</Link>
                          {' '}y el{' '}
                          <Link to="/privacy" target="_blank" className="text-[#F05D5E] font-medium hover:underline">Aviso de Privacidad</Link>.
                          Entiendo que Bookvia es un intermediario tecnologico, que yo soy el responsable del servicio prestado a mis clientes,
                          y que las propinas y facturas (CFDI) de las citas las gestiono directamente con ellos.
                        </>
                      ) : (
                        <>
                          As the business owner, I accept the{' '}
                          <Link to="/terms" target="_blank" className="text-[#F05D5E] font-medium hover:underline">Terms</Link>
                          {' '}and{' '}
                          <Link to="/privacy" target="_blank" className="text-[#F05D5E] font-medium hover:underline">Privacy Notice</Link>.
                          I understand that Bookvia is a technology intermediary, that I am the responsible party for the service
                          provided to my clients, and that tips and invoices (CFDI) are handled directly with them.
                        </>
                      )}
                    </span>
                  </label>

                  {/* ── Phase T — Datos bancarios: moved below the booking
                     policy card so it only appears after the business
                     explicitly picked "Requiero anticipo". When they
                     choose "No requiero anticipo" we don't ask for CLABE
                     at all — there's nothing we would ever deposit. */}

                  {/* ── Política de reservas y cancelación ─────── */}
                  <div className="pt-4 space-y-4">
                    <h3 className="font-heading font-semibold text-sm flex items-center gap-2">
                      <CalendarX className="h-4 w-4 text-[#F05D5E]" />
                      {language === 'es' ? 'Política de reservas' : 'Booking policy'}
                    </h3>

                    {/* Opción 1: Con anticipo */}
                    <div
                      className={`rounded-xl border-2 p-4 cursor-pointer transition-all ${formData.requires_deposit ? 'border-[#F05D5E] bg-[#F05D5E]/5' : 'border-border hover:border-muted-foreground/30'}`}
                      onClick={() => setFormData(prev => ({ ...prev, requires_deposit: true }))}
                      data-testid="option-with-deposit"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.requires_deposit ? 'border-[#F05D5E]' : 'border-muted-foreground/40'}`}>
                          {formData.requires_deposit && <div className="w-2.5 h-2.5 rounded-full bg-[#F05D5E]" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">
                            {(() => {
                              // For businesses that register under the
                              // "Eventos" super-group, the deposit is used
                              // to hold the event date — not to pay a
                              // regular booking. Adjust the copy so the
                              // owner understands the semantic difference.
                              const selectedCat = categories.find(c => c.id === formData.category_id);
                              const isEventsCategory = EVENTS_ENABLED && (
                                selectedCat?.super_group === 'eventos' ||
                                ['eventos-celebraciones', 'fotografia-video'].includes(selectedCat?.slug)
                              );
                              if (isEventsCategory) {
                                return language === 'es'
                                  ? 'Requiero anticipo para separar la fecha'
                                  : 'I require a deposit to hold the date';
                              }
                              return language === 'es'
                                ? 'Requiero anticipo para las reservas'
                                : 'I require a deposit for bookings';
                            })()}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {language === 'es' ? 'El cliente paga un anticipo al reservar' : 'Customer pays a deposit when booking'}
                          </p>
                        </div>
                      </div>

                      {formData.requires_deposit && (
                        <div className="mt-4 space-y-4 pl-8" onClick={(e) => e.stopPropagation()}>
                          {/* Monto del anticipo */}
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <Label htmlFor="deposit_amount" className="text-sm">
                                <Banknote className="inline h-3.5 w-3.5 mr-1 text-muted-foreground" />
                                {language === 'es' ? 'Monto del anticipo (MXN)' : 'Deposit amount (MXN)'}
                              </Label>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button type="button" className="text-muted-foreground hover:text-[#F05D5E] transition-colors" data-testid="help-deposit-amount">
                                    <HelpCircle className="h-4 w-4" />
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 text-sm" side="top">
                                  <p className="font-medium mb-1">{language === 'es' ? 'Monto del anticipo' : 'Deposit amount'}</p>
                                  <p className="text-muted-foreground text-xs leading-relaxed">
                                    {language === 'es'
                                      ? 'Cantidad que el cliente paga al reservar para confirmar su cita. El resto se paga en el establecimiento. Minimo $100 MXN. Se descuenta 8.5% como cuota de procesamiento: por cada $100 de anticipo recibiras $91.50.'
                                      : 'Amount the customer pays when booking to confirm. The rest is paid at the venue. Minimum $100 MXN. A 8.5% processing fee is deducted: for every $100 deposit you receive $91.50.'}
                                  </p>
                                </PopoverContent>
                              </Popover>
                            </div>
                            <Input
                              id="deposit_amount"
                              name="deposit_amount"
                              type="number"
                              min="100"
                              value={formData.deposit_amount}
                              onChange={handleChange}
                              className="h-10 w-36"
                              data-testid="deposit-amount-input"
                            />
                            <p className="text-xs text-muted-foreground">
                              {language === 'es' 
                                ? `Minimo $100 MXN. Recibiras ${formatCurrencyMXN((Number(formData.deposit_amount) || 0) * 0.915)} por cada anticipo (8.5% cuota procesamiento).`
                                : `Minimum $100 MXN. You'll receive ${formatCurrencyMXN((Number(formData.deposit_amount) || 0) * 0.915)} per deposit (8.5% processing fee).`}
                            </p>
                          </div>

                          {/* CLABE moved to always-visible section above (Phase T) */}

                          {/* Margen de cancelación con anticipo
                              For "Eventos" businesses this field means
                              "how many days after the hold does the client
                              have to sign the contract before the date is
                              released" — not a cancellation window in
                              hours. We swap label, units and help copy. */}
                          {(() => {
                            const selectedCat = categories.find(c => c.id === formData.category_id);
                            const isEventsCategory = EVENTS_ENABLED && (
                              selectedCat?.super_group === 'eventos' ||
                              ['eventos-celebraciones', 'fotografia-video'].includes(selectedCat?.slug)
                            );
                            const labelEs = isEventsCategory
                              ? 'Días de margen para firmar el contrato'
                              : 'Margen de cancelación';
                            const labelEn = isEventsCategory
                              ? 'Days to sign the contract'
                              : 'Cancellation window';
                            const popTitleEs = isEventsCategory
                              ? 'Días para firmar el contrato'
                              : 'Margen de cancelación y devolución';
                            const popTitleEn = isEventsCategory
                              ? 'Contract signing deadline'
                              : 'Cancellation and refund margin';
                            const popCopyEs = isEventsCategory
                              ? 'Cuántos días tiene el cliente para firmar el contrato desde que separa la fecha.\n\nSi no firma en ese tiempo, la fecha se libera automáticamente y no se reembolsa el anticipo.'
                              : 'Hasta cuantas horas antes de la cita el cliente puede cancelar y recibir devolucion del anticipo.\n\nMinimo: 1 hora. Maximo: 72 horas (3 dias).\n\nEste limite protege a tus clientes y evita disputas.';
                            const popCopyEn = isEventsCategory
                              ? 'How many days the client has to sign the contract after paying the hold fee. If not signed in time, the date is released automatically and the hold fee is non-refundable.'
                              : 'How many hours before the appointment a client may cancel and get the deposit refunded.\n\nMinimum 1 hour, maximum 72 hours (3 days). This cap protects your clients and prevents disputes.';
                            const options = isEventsCategory
                              ? [3, 5, 7, 10, 15, 20, 30]
                              : [1, 2, 4, 6, 12, 24, 48, 72];
                            const unitLabel = (n) => isEventsCategory
                              ? (language === 'es' ? (n === 1 ? 'día' : 'días') : (n === 1 ? 'day' : 'days'))
                              : (language === 'es' ? (n === 1 ? 'hora' : 'horas') : (n === 1 ? 'hour' : 'hours'));
                            const helperEs = isEventsCategory
                              ? `El cliente tiene ${formData.cancellation_hours || (isEventsCategory ? 15 : 24)} día(s) para firmar el contrato`
                              : `El cliente puede cancelar hasta ${formData.cancellation_hours || 24} hora(s) antes y recibir el reembolso`;
                            const helperEn = isEventsCategory
                              ? `Client has ${formData.cancellation_hours || 15} day(s) to sign the contract`
                              : `Customer can cancel up to ${formData.cancellation_hours || 24} hour(s) before for a refund`;

                            return (
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-1.5">
                                  <Label htmlFor="cancellation_hours_deposit" className="text-sm">
                                    <CalendarX className="inline h-3.5 w-3.5 mr-1 text-muted-foreground" />
                                    {language === 'es' ? labelEs : labelEn}
                                  </Label>
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button type="button" className="text-muted-foreground hover:text-[#F05D5E] transition-colors" data-testid="help-cancellation-deposit">
                                        <HelpCircle className="h-4 w-4" />
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-80 text-sm" side="top">
                                      <p className="font-medium mb-1">{language === 'es' ? popTitleEs : popTitleEn}</p>
                                      <p className="text-muted-foreground text-xs leading-relaxed whitespace-pre-line">
                                        {language === 'es' ? popCopyEs : popCopyEn}
                                      </p>
                                    </PopoverContent>
                                  </Popover>
                                </div>
                                <select
                                  id="cancellation_hours_deposit"
                                  name="cancellation_hours"
                                  value={formData.cancellation_hours || (isEventsCategory ? 15 : 24)}
                                  onChange={handleChange}
                                  className="h-10 w-40 rounded-md border border-input bg-background px-3 text-sm"
                                  data-testid="cancellation-hours-deposit-input"
                                >
                                  {options.map((n) => (
                                    <option key={n} value={n}>
                                      {n} {unitLabel(n)}
                                    </option>
                                  ))}
                                </select>
                                <p className="text-xs text-muted-foreground">
                                  {language === 'es' ? helperEs : helperEn}
                                </p>
                              </div>
                            );
                          })()}

                          {/* Calendario fijo de liquidaciones + acceso al desglose de comisiones */}
                          <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-4 space-y-3" data-testid="commission-info-block">
                            <h4 className="text-sm font-semibold flex items-center gap-1.5">
                              <CreditCard className="h-4 w-4 text-[#F05D5E]" />
                              {language === 'es' ? 'Comisiones y liquidaciones' : 'Fees and payouts'}
                            </h4>

                            <div className="rounded-md border-2 border-blue-200 bg-blue-50/60 p-3 flex items-start gap-2.5">
                              <Calendar className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                              <div className="text-xs leading-relaxed">
                                <p className="font-semibold text-blue-900 mb-0.5">
                                  {language === 'es' ? 'Calendario fijo' : 'Fixed calendar'}
                                </p>
                                <p className="text-blue-900/80">
                                  {language === 'es'
                                    ? 'Corte el día 20 de cada mes · Depósito el día 1° del mes siguiente. Este calendario es igual para todos los negocios.'
                                    : 'Cutoff day 20 each month · Payout on the 1st of the following month. Same schedule for every business.'}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-start justify-between gap-3 rounded-md border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-700 p-3">
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium">
                                  {language === 'es'
                                    ? '¿Qué se cobra exactamente?'
                                    : 'What exactly is charged?'}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                                  {language === 'es'
                                    ? 'Revisa el desglose completo: qué paga el cliente, qué retiene Stripe, qué se queda Bookvia y cuánto recibes tú neto en tu CLABE.'
                                    : 'Review the full breakdown: what the client pays, what Stripe keeps, what Bookvia keeps and your net payout.'}
                                </p>
                                {formData.commission_terms_accepted && (
                                  <div className="flex items-center gap-1.5 mt-2">
                                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                                    <span className="text-[11px] text-emerald-700 font-medium">
                                      {language === 'es' ? 'Términos aceptados' : 'Terms accepted'}
                                      <span className="text-emerald-700/60 ml-1">
                                        ({formData.commission_terms_version})
                                      </span>
                                    </span>
                                  </div>
                                )}
                              </div>
                              <Button
                                type="button"
                                variant={formData.commission_terms_accepted ? 'outline' : 'default'}
                                size="sm"
                                onClick={() => setShowCommissionModal(true)}
                                className={formData.commission_terms_accepted ? '' : 'bg-[#F05D5E] hover:bg-[#F05D5E]/90 text-white'}
                                data-testid="open-commission-breakdown-btn"
                              >
                                <FileText className="h-3.5 w-3.5 mr-1" />
                                {formData.commission_terms_accepted
                                  ? (language === 'es' ? 'Ver desglose' : 'View breakdown')
                                  : (language === 'es' ? 'Ver desglose completo' : 'View full breakdown')}
                              </Button>
                            </div>

                            <p className="text-[11px] text-muted-foreground/80 leading-snug">
                              {language === 'es'
                                ? 'Bookvia cobra un fee fijo al cliente y un costo de procesamiento Stripe estimado al negocio. Podrás consultar todos los movimientos y anticipos recibidos en tu panel de estado de cuenta dentro de Bookvia.'
                                : 'Bookvia charges a fixed fee to the client and an estimated Stripe processing cost to the business. All movements and deposits received can be viewed in your Bookvia account dashboard.'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Opción 2: Sin anticipo */}
                    <div
                      className={`rounded-xl border-2 p-4 cursor-pointer transition-all ${!formData.requires_deposit ? 'border-[#F05D5E] bg-[#F05D5E]/5' : 'border-border hover:border-muted-foreground/30'}`}
                      onClick={() => setFormData(prev => ({ ...prev, requires_deposit: false }))}
                      data-testid="option-without-deposit"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${!formData.requires_deposit ? 'border-[#F05D5E]' : 'border-muted-foreground/40'}`}>
                          {!formData.requires_deposit && <div className="w-2.5 h-2.5 rounded-full bg-[#F05D5E]" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm">
                            {language === 'es' ? 'No requiero anticipo' : 'No deposit required'}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {language === 'es' ? 'El cliente reserva sin costo previo' : 'Customer books with no upfront cost'}
                          </p>
                        </div>
                      </div>

                      {!formData.requires_deposit && (
                        <div className="mt-4 pl-8" onClick={(e) => e.stopPropagation()}>
                          {/* Margen de cancelación sin anticipo.
                              Same "Eventos vs regular" swap as the deposit
                              branch above. */}
                          {(() => {
                            const selectedCat = categories.find(c => c.id === formData.category_id);
                            const isEventsCategory = EVENTS_ENABLED && (
                              selectedCat?.super_group === 'eventos' ||
                              ['eventos-celebraciones', 'fotografia-video'].includes(selectedCat?.slug)
                            );
                            const labelEs = isEventsCategory
                              ? 'Días de margen para firmar el contrato'
                              : 'Margen de cancelación';
                            const labelEn = isEventsCategory
                              ? 'Days to sign the contract'
                              : 'Cancellation window';
                            const popCopyEs = isEventsCategory
                              ? 'Cuántos días tiene el cliente para firmar el contrato desde que separa la fecha.\n\nSi no firma en ese tiempo, la fecha se libera automáticamente.'
                              : 'Cuántas horas antes de la cita el cliente puede cancelar sin penalización.\n\nMínimo 1 hora, máximo 72 horas (3 días).\n\nSi cancela después de ese tiempo, la cancelación se marca como tardía o no-show.';
                            const popCopyEn = isEventsCategory
                              ? 'How many days the client has to sign the contract after paying the hold fee. If not signed in time, the date is released automatically.'
                              : 'How many hours before the appointment a client can cancel without penalty.\n\nMin 1 hour, max 72 hours (3 days). Late cancellations may be flagged as no-show.';
                            const options = isEventsCategory
                              ? [3, 5, 7, 10, 15, 20, 30]
                              : [1, 2, 4, 6, 12, 24, 48, 72];
                            const unitLabel = (n) => isEventsCategory
                              ? (language === 'es' ? (n === 1 ? 'día' : 'días') : (n === 1 ? 'day' : 'days'))
                              : (language === 'es' ? (n === 1 ? 'hora' : 'horas') : (n === 1 ? 'hour' : 'hours'));
                            const helperEs = isEventsCategory
                              ? `El cliente tiene ${formData.cancellation_hours || 15} día(s) para firmar el contrato`
                              : `El cliente puede cancelar hasta ${formData.cancellation_hours || 24} hora(s) antes sin penalizacion`;
                            const helperEn = isEventsCategory
                              ? `Client has ${formData.cancellation_hours || 15} day(s) to sign the contract`
                              : `Customer can cancel up to ${formData.cancellation_hours || 24} hour(s) before without penalty`;

                            return (
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-1.5">
                                  <Label htmlFor="cancellation_hours_no_deposit" className="text-sm">
                                    <CalendarX className="inline h-3.5 w-3.5 mr-1 text-muted-foreground" />
                                    {language === 'es' ? labelEs : labelEn}
                                  </Label>
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button type="button" className="text-muted-foreground hover:text-[#F05D5E] transition-colors" data-testid="help-cancellation-no-deposit">
                                        <HelpCircle className="h-4 w-4" />
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-80 text-sm" side="top">
                                      <p className="font-medium mb-1">{language === 'es' ? labelEs : labelEn}</p>
                                      <p className="text-muted-foreground text-xs leading-relaxed whitespace-pre-line">
                                        {language === 'es' ? popCopyEs : popCopyEn}
                                      </p>
                                    </PopoverContent>
                                  </Popover>
                                </div>
                                <select
                                  id="cancellation_hours_no_deposit"
                                  name="cancellation_hours"
                                  value={formData.cancellation_hours || (isEventsCategory ? 15 : 24)}
                                  onChange={handleChange}
                                  className="h-10 w-40 rounded-md border border-input bg-background px-3 text-sm"
                                  data-testid="cancellation-hours-no-deposit-input"
                                >
                                  {options.map((n) => (
                                    <option key={n} value={n}>
                                      {n} {unitLabel(n)}
                                    </option>
                                  ))}
                                </select>
                                <p className="text-xs text-muted-foreground">
                                  {language === 'es' ? helperEs : helperEn}
                                </p>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── Phase T — Datos bancarios ─ visible solo si el
                     negocio marcó "Requiero anticipo". Si eligió "No
                     requiero anticipo", no pedimos CLABE: no hay nada que
                     depositarle. */}
                  {formData.requires_deposit && (
                    <div className="pt-4 space-y-4">
                      <div className="rounded-2xl border-2 border-[#F05D5E]/25 bg-gradient-to-br from-[#FFF4EC] to-[#FFE4D6] dark:from-[#1F1613] dark:to-[#2A1D18] p-5">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="h-9 w-9 rounded-xl bg-[#F05D5E] flex items-center justify-center shrink-0">
                            <Banknote className="h-5 w-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading font-bold text-base leading-tight">
                              {language === 'es'
                                ? 'Datos bancarios (para recibir tus anticipos)'
                                : 'Bank details (to receive your funds)'}
                            </h3>
                            <p className="text-xs text-foreground/70 mt-1 leading-relaxed">
                              {language === 'es'
                                ? 'Aquí depositaremos el dinero de los anticipos que cobres a tus clientes. Los depósitos se hacen el día 1° de cada mes según lo cobrado hasta el día 20.'
                                : 'This is where we\'ll deposit the funds from the deposits you charge your clients. Payouts happen on the 1st of each month based on what was collected through the 20th.'}
                            </p>
                          </div>
                        </div>

                        {/* Trial reassurance */}
                        <div className="flex items-start gap-2 rounded-xl bg-white/60 dark:bg-black/20 backdrop-blur px-3 py-2.5 mb-4 border border-white/40">
                          <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                          <p className="text-xs leading-snug">
                            {language === 'es' ? (
                              <>
                                <span className="font-semibold">Por ahora NO te pediremos tu tarjeta.</span> Tienes 30 días completos gratis. Al terminar podrás decidir si continuar.
                              </>
                            ) : (
                              <>
                                <span className="font-semibold">We won&apos;t ask for your card yet.</span> You get 30 full days free. At the end, you decide whether to continue.
                              </>
                            )}
                          </p>
                        </div>

                        {/* CLABE */}
                        <div className="space-y-1.5 mb-3">
                          <Label htmlFor="clabe" className="text-sm font-medium">
                            {formData.country === 'US'
                              ? 'Routing + Account'
                              : (language === 'es' ? 'CLABE interbancaria (18 dígitos) *' : 'CLABE (18 digits) *')}
                          </Label>
                          <Input
                            id="clabe"
                            name="clabe"
                            placeholder={formData.country === 'US' ? '021000021 / 123456789' : '012345678901234567'}
                            value={formData.clabe}
                            onChange={(e) => setFormData(prev => ({ ...prev, clabe: formData.country === 'US' ? e.target.value : e.target.value.replace(/\D/g, '') }))}
                            className="h-11 font-mono tracking-wide"
                            maxLength={formData.country === 'US' ? 25 : 18}
                            required
                            data-testid="clabe-input"
                          />
                        </div>

                        {/* Titular */}
                        <div className="space-y-1.5 mb-3">
                          <Label htmlFor="bank_holder_name" className="text-sm font-medium">
                            {language === 'es' ? 'Titular de la cuenta *' : 'Account holder *'}
                          </Label>
                          <Input
                            id="bank_holder_name"
                            name="bank_holder_name"
                            placeholder={language === 'es' ? 'Nombre completo tal cual aparece en el banco' : 'Full name as it appears at the bank'}
                            value={formData.bank_holder_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, bank_holder_name: e.target.value }))}
                            className="h-11"
                            maxLength={100}
                            required
                            data-testid="bank-holder-name-input"
                          />
                        </div>

                        {/* Banco */}
                        <div className="space-y-1.5">
                          <Label htmlFor="bank_name" className="text-sm font-medium">
                            {language === 'es' ? 'Banco *' : 'Bank *'}
                          </Label>
                          <Select
                            value={formData.bank_name}
                            onValueChange={(v) => setFormData(prev => ({ ...prev, bank_name: v }))}
                          >
                            <SelectTrigger className="h-11" data-testid="bank-name-select">
                              <SelectValue placeholder={language === 'es' ? 'Selecciona tu banco' : 'Select your bank'} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="BBVA">BBVA</SelectItem>
                              <SelectItem value="Banorte">Banorte</SelectItem>
                              <SelectItem value="Santander">Santander</SelectItem>
                              <SelectItem value="HSBC">HSBC</SelectItem>
                              <SelectItem value="Banamex">Banamex / Citibanamex</SelectItem>
                              <SelectItem value="Scotiabank">Scotiabank</SelectItem>
                              <SelectItem value="Inbursa">Inbursa</SelectItem>
                              <SelectItem value="Azteca">Banco Azteca</SelectItem>
                              <SelectItem value="BanBajio">BanBajío</SelectItem>
                              <SelectItem value="Banregio">Banregio</SelectItem>
                              <SelectItem value="Afirme">Afirme</SelectItem>
                              <SelectItem value="Compartamos">Compartamos</SelectItem>
                              <SelectItem value="Nu">Nu México</SelectItem>
                              <SelectItem value="Klar">Klar</SelectItem>
                              <SelectItem value="Hey Banco">Hey Banco</SelectItem>
                              <SelectItem value="Mercado Pago">Mercado Pago</SelectItem>
                              <SelectItem value="STP">STP</SelectItem>
                              <SelectItem value="Otro">
                                {language === 'es' ? 'Otro banco' : 'Other bank'}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 4: Subscription */}
              {currentStep === 3 && (
                <div className="space-y-6" data-testid="step-trial-success">
                  <div className="text-center space-y-3 py-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-green-500/10">
                      <CheckCircle2 className="h-8 w-8 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-heading font-bold">
                      {language === 'es' ? '¡Tu prueba gratis empezó!' : 'Your free trial has started!'}
                    </h2>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      {language === 'es'
                        ? 'Tienes 30 días de acceso completo a Bookvia. Sin costo, sin tarjeta.'
                        : 'You have 30 days of full Bookvia access. No cost, no card required.'}
                    </p>
                  </div>

                  {/* Trial benefits */}
                  <div className="rounded-2xl border border-green-500/30 bg-green-50 dark:bg-green-950/20 p-5 space-y-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm">{language === 'es' ? 'Reservas ilimitadas' : 'Unlimited bookings'}</p>
                        <p className="text-xs text-muted-foreground">
                          {language === 'es' ? 'Acepta clientes 24/7 durante los 30 días' : 'Accept clients 24/7 for the full 30 days'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm">{language === 'es' ? 'Cobros de anticipo activos' : 'Deposit charges active'}</p>
                        <p className="text-xs text-muted-foreground">
                          {language === 'es' ? 'Los depositaremos en la cuenta que registraste' : 'We\'ll deposit them to your registered account'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm">{language === 'es' ? 'Panel de negocio completo' : 'Full business dashboard'}</p>
                        <p className="text-xs text-muted-foreground">
                          {language === 'es' ? 'Estadísticas, calendario, catálogo y más' : 'Stats, calendar, catalog and more'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* What happens after 30 days */}
                  <div className="rounded-xl border border-border bg-muted/30 p-4">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                      {language === 'es' ? '¿Qué pasa al terminar los 30 días?' : 'What happens after 30 days?'}
                    </p>
                    <p className="text-sm leading-relaxed">
                      {language === 'es'
                        ? 'Te avisaremos antes de que termine. Si quieres seguir usando Bookvia, agregarás tu tarjeta desde la web y se cobrarán $49.99 MXN al mes automáticamente. Si no, tu negocio queda inactivo (pero tus datos se preservan).'
                        : 'We\'ll notify you before it ends. To continue, add your card on the web and $49.99 MXN/month will be charged automatically. If not, your business becomes inactive (but your data is preserved).'}
                    </p>
                  </div>

                  {/* Admin approval notice */}
                  <div className="text-center bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mx-auto mb-2" />
                    <p className="text-sm text-amber-800 dark:text-amber-200 leading-relaxed">
                      {language === 'es'
                        ? 'Tu negocio quedará pendiente de aprobación por el administrador antes de aparecer públicamente en la plataforma.'
                        : 'Your business will be pending admin approval before appearing publicly on the platform.'}
                    </p>
                  </div>

                  {/* CTA */}
                  <div className="space-y-3">
                    <Button
                      type="button"
                      className="w-full btn-coral h-14 text-base gap-2"
                      onClick={() => navigate('/business/dashboard')}
                      data-testid="go-to-dashboard-button"
                    >
                      {language === 'es' ? 'Ir a mi panel' : 'Go to my dashboard'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Navigation buttons - hidden on subscription step (has its own) */}
              {currentStep < 3 && (
              <div className="flex justify-between mt-8 pt-4 border-t">
                {currentStep > 0 ? (
                  <Button type="button" variant="outline" onClick={handleBack} className="h-12">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {language === 'es' ? 'Anterior' : 'Previous'}
                  </Button>
                ) : (
                  <div />
                )}
                
                <Button type="button" onClick={handleNext} className="h-12 btn-coral" disabled={loading}>
                  {loading 
                    ? (language === 'es' ? 'Procesando...' : 'Processing...')
                    : currentStep === 2
                    ? (language === 'es' ? 'Registrar negocio' : 'Register business')
                    : (language === 'es' ? 'Siguiente' : 'Next')}
                  {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
                </Button>
              </div>
              )}
            </form>

            <p className="text-xs text-muted-foreground text-center mt-6">
              {language === 'es'
                ? '¿Ya tienes cuenta? '
                : 'Already have an account? '}
              <Link to="/business/login" className="text-[#F05D5E] hover:underline">
                {language === 'es' ? 'Inicia sesión' : 'Log in'}
              </Link>
            </p>
          </CardContent>
        </Card>

        {/* Info card */}
        <Card className="mt-6 border-0 shadow-lg bg-gradient-to-r from-[#0F4C81]/10 to-[#F05D5E]/10">
          <CardContent className="p-6">
            <h3 className="font-heading font-bold mb-3">
              {language === 'es' ? '¿Por qué unirte a Bookvia?' : 'Why join Bookvia?'}
            </h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                {language === 'es' ? '1 mes gratis de suscripción' : '1 month free subscription'}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                {language === 'es' ? 'Pequeñas comisiones' : 'Low fees'}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                {language === 'es' ? 'Panel de gestión completo' : 'Complete management dashboard'}
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                {language === 'es' ? 'Protección contra no-shows' : 'No-show protection'}
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Commission breakdown modal — triggered from Step 4 "Sí pedir anticipo" */}
      <CommissionBreakdownModal
        open={showCommissionModal}
        onOpenChange={(v) => {
          setShowCommissionModal(v);
          if (!v) setContinueAfterAccept(false);  // user closed without accepting
        }}
        language={language}
        initialAmount={Number(formData.deposit_amount) || 500}
        isEventsCategory={(() => {
          // Same detection logic as the labels in step 4 — keeps copy
          // consistent between the deposit label and this modal.
          // Gated by EVENTS_ENABLED so the eventos disclaimer stays hidden
          // while the vertical is disabled.
          if (!EVENTS_ENABLED) return false;
          const selectedCat = categories.find(c => c.id === formData.category_id);
          return (
            selectedCat?.super_group === 'eventos' ||
            ['eventos-celebraciones', 'fotografia-video'].includes(selectedCat?.slug)
          );
        })()}
        onAccept={({ version, hash, snapshot }) => {
          setFormData(prev => ({
            ...prev,
            commission_terms_accepted: true,
            commission_terms_version: version,
            commission_terms_hash: hash,
            commission_terms_snapshot: snapshot,
          }));
          toast.success(language === 'es'
            ? 'Términos de comisiones aceptados'
            : 'Fee terms accepted');
          // If the modal was opened from the "Registrar negocio" gate,
          // automatically resume registration after acceptance — single flow.
          if (continueAfterAccept) {
            setContinueAfterAccept(false);
            // Pass the just-accepted terms inline to bypass any state-flush race
            handleRegister({
              commission_terms_accepted: true,
              commission_terms_version: version,
              commission_terms_hash: hash,
              commission_terms_snapshot: snapshot,
            });
          }
        }}
      />
    </div>
  );
}
